/**
 * generateAutodocReports.ts - documents AutoDoc complémentaires :
 *   - Microsoft Azure (posture Azure)
 *   - Politique de sécurité générale (modèle ISMS pré-rempli)
 *   - Registre des traitements de données (RGPD art. 30)
 * Mise en forme homogène via autodocCommon.
 */
import { Document, Packer, Paragraph, Table, TableRow, WidthType, HeadingLevel, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import { AZURE_CONTROLS } from '../domain/azureControls';
import {
  AUTODOC_STYLES, buildCover, buildToc, buildIntro, cell, headerCell, statusFr, statusColor, safeName, today,
} from './autodocCommon';

type AzureCtrl = typeof AZURE_CONTROLS[number];

export interface AutodocMeta {
  orgName:       string;
  tenantDomain?: string;
  tenantId?:     string;
  lastScanDate?: string;
  logo?:         string | null;
}
export interface AutodocHit { status: string; message?: string; date?: string; }

function para(text: string, size = 20): Paragraph {
  return new Paragraph({ children: [new TextRun({ text, size })] });
}

// ===========================================================================
//  1. Microsoft Azure
// ===========================================================================
export async function downloadAzureDocx(params: { meta: AutodocMeta; scanByControl: Map<string, AutodocHit> }): Promise<void> {
  const { meta, scanByControl } = params;
  const cover = await buildCover({
    brandLine: 'NIS365 AUDITOR · AutoDoc',
    title: 'Documentation Plateforme Microsoft Azure',
    subtitle: 'Posture de sécurité Azure — base documentaire NIS2',
    orgName: meta.orgName, logo: meta.logo,
    footnote: 'Document généré automatiquement à partir du dernier scan Azure.',
  });
  const intro = buildIntro({
    purpose: "Décrire l'état de configuration de l'environnement Microsoft Azure (réseau, données, calcul, identité, journalisation) au regard des exigences NIS2.",
    audience: 'RSSI, équipe cloud/infrastructure, auditeur.',
    scope: meta.tenantDomain ? `Tenant ${meta.tenantDomain}` : 'Souscription(s) Azure connectée(s).',
  });

  const byFamily = new Map<string, AzureCtrl[]>();
  for (const c of AZURE_CONTROLS) {
    const arr = byFamily.get(c.family) ?? [];
    arr.push(c); byFamily.set(c.family, arr);
  }
  const detail: (Paragraph | Table)[] = [
    new Paragraph({ text: 'Configuration par domaine', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
  ];
  for (const fam of [...byFamily.keys()].sort()) {
    detail.push(new Paragraph({ text: fam, heading: HeadingLevel.HEADING_3 }));
    detail.push(new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({ tableHeader: true, children: [headerCell('ID', 14), headerCell('Contrôle', 44), headerCell('État', 18), headerCell('Constat', 24)] }),
        ...byFamily.get(fam)!.map((c) => {
          const h = scanByControl.get(c.id.toLowerCase());
          return new TableRow({ children: [
            cell(c.id, { bold: true }), cell(c.title),
            cell(statusFr(h?.status), { bold: true, color: statusColor(h?.status) }),
            cell(h?.message?.trim() || (h ? '—' : 'Non couvert par le dernier scan')),
          ] });
        }),
      ],
    }));
    detail.push(new Paragraph({ children: [] }));
  }

  const doc = new Document({ creator: 'NIS365 Auditor', title: `Doc Azure - ${meta.orgName}`, features: { updateFields: true }, styles: AUTODOC_STYLES, sections: [{ children: [...cover, ...buildToc(), ...intro, ...detail] }] });
  saveAs(await Packer.toBlob(doc), `Doc_Azure_${safeName(meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}

// ===========================================================================
//  2. Politique de sécurité générale (modèle)
// ===========================================================================
export async function downloadSecurityPolicyDocx(params: { meta: AutodocMeta }): Promise<void> {
  const { meta } = params;
  const cover = await buildCover({
    brandLine: 'NIS365 AUDITOR · AutoDoc',
    title: 'Politique de sécurité de l’information',
    subtitle: 'Modèle conforme NIS2 (art. 21) — à adapter',
    orgName: meta.orgName, logo: meta.logo,
    footnote: 'Modèle pré-rempli à valider et compléter par la direction.',
  });
  const intro = buildIntro({
    purpose: "Définir les principes et règles de sécurité de l'information de l'organisation, en réponse aux mesures de gestion des risques NIS2.",
    audience: 'Direction, RSSI, ensemble du personnel.',
    scope: `Systèmes d'information de ${meta.orgName}, incluant Microsoft 365 et Azure.`,
  });

  const sections: { title: string; body: string }[] = [
    { title: '1. Objet & engagement de la direction', body: `La direction de ${meta.orgName} s'engage à protéger la confidentialité, l'intégrité et la disponibilité de l'information. La présente politique est approuvée, communiquée et revue au moins annuellement.` },
    { title: '2. Gouvernance & rôles', body: "Un responsable de la sécurité (RSSI) est désigné. Les rôles et responsabilités en matière de sécurité sont définis et documentés (RACI)." },
    { title: '3. Gestion des risques', body: "Une analyse de risque est menée et tenue à jour. Les mesures de traitement sont priorisées et suivies dans un plan d'action." },
    { title: '4. Contrôle d’accès & identités', body: "L'authentification multifacteur est imposée. L'accès suit le moindre privilège. Les comptes à privilèges sont limités, tracés et revus régulièrement." },
    { title: '5. Protection des données', body: "Les données sensibles sont classifiées, chiffrées au repos et en transit, et protégées par des politiques DLP et de rétention." },
    { title: '6. Journalisation & détection', body: "Les événements de sécurité sont journalisés, conservés et supervisés. Des alertes sont configurées sur les événements critiques." },
    { title: '7. Gestion des incidents', body: "Les incidents sont enregistrés, qualifiés et déclarés dans les délais NIS2. Un processus de notification et de retour d'expérience est en place." },
    { title: '8. Continuité d’activité', body: "Des sauvegardes et un plan de continuité/reprise sont maintenus et testés périodiquement." },
    { title: '9. Sensibilisation & tiers', body: "Le personnel est formé à la sécurité. Les fournisseurs critiques font l'objet d'exigences contractuelles de sécurité." },
    { title: '10. Révision', body: 'La présente politique est revue au minimum une fois par an, ou après tout incident ou changement majeur.' },
  ];
  const chapters: Paragraph[] = [];
  for (const s of sections) {
    chapters.push(new Paragraph({ text: s.title, heading: HeadingLevel.HEADING_3 }));
    chapters.push(para(s.body));
  }
  const body: (Paragraph | Table)[] = [
    new Paragraph({ text: 'Politique', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    ...chapters,
    new Paragraph({ children: [] }),
    para(`Approuvée le ${today()} — ${meta.orgName}.`, 18),
  ];

  const doc = new Document({ creator: 'NIS365 Auditor', title: `Politique securite - ${meta.orgName}`, features: { updateFields: true }, styles: AUTODOC_STYLES, sections: [{ children: [...cover, ...buildToc(), ...intro, ...body] }] });
  saveAs(await Packer.toBlob(doc), `Politique_Securite_${safeName(meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}

// ===========================================================================
//  3. Registre des traitements de données (RGPD art. 30)
// ===========================================================================
export async function downloadDataRegisterDocx(params: { meta: AutodocMeta }): Promise<void> {
  const { meta } = params;
  const cover = await buildCover({
    brandLine: 'NIS365 AUDITOR · AutoDoc',
    title: 'Registre des traitements de données',
    subtitle: 'RGPD art. 30 — modèle pré-rempli (M365 / Azure)',
    orgName: meta.orgName, logo: meta.logo,
    footnote: 'Modèle à compléter et valider par le DPO / responsable de traitement.',
  });
  const intro = buildIntro({
    purpose: "Recenser les activités de traitement de données à caractère personnel de l'organisation (art. 30 RGPD), sur la base des services Microsoft 365 et Azure.",
    audience: 'DPO, responsable de traitement, RSSI.',
    scope: `Traitements opérés via l'environnement Microsoft de ${meta.orgName}.`,
  });

  // Lignes pré-remplies (à adapter par le client).
  const rows: string[][] = [
    ['Messagerie (Exchange Online)', 'Communication professionnelle', 'Identité, contenu des messages', 'Employés, contacts', 'Intérêt légitime / contrat', 'UE (M365)', '≤ durée d’emploi + archivage légal'],
    ['Collaboration (SharePoint / OneDrive)', 'Stockage et partage de documents', 'Documents, métadonnées', 'Employés, clients', 'Intérêt légitime / contrat', 'UE (M365)', 'Selon politique de rétention'],
    ['Teams', 'Réunions & messagerie instantanée', 'Identité, contenu, présence', 'Employés, invités', 'Intérêt légitime', 'UE (M365)', 'Selon politique de rétention'],
    ['Comptes & annuaire (Entra ID)', 'Gestion des identités et accès', 'Identité, rôles, journaux de connexion', 'Employés, invités', 'Obligation légale / contrat', 'UE (Entra ID)', 'Durée du compte + logs'],
    ['Journalisation & audit', 'Sécurité et traçabilité', 'Journaux d’activité, adresses IP', 'Employés', 'Intérêt légitime / obligation', 'UE', '≥ 6 mois (recommandé)'],
    ['Ressources Azure', 'Hébergement applicatif / données', 'Selon application', 'Selon application', 'Selon finalité', 'Région Azure UE', 'Selon finalité'],
  ];

  const table = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: [
      new TableRow({ tableHeader: true, children: [
        headerCell('Traitement', 18), headerCell('Finalité', 16), headerCell('Catégories de données', 18),
        headerCell('Personnes concernées', 14), headerCell('Base légale', 12), headerCell('Localisation', 10), headerCell('Conservation', 12),
      ] }),
      ...rows.map((r) => new TableRow({ children: r.map((v) => cell(v)) })),
    ],
  });

  const body: (Paragraph | Table)[] = [
    new Paragraph({ text: 'Registre des activités de traitement', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }),
    para('Responsable du traitement : ' + meta.orgName + '. Compléter les colonnes (sous-traitants, transferts hors UE, mesures de sécurité) selon votre contexte.'),
    new Paragraph({ children: [] }),
    table,
    new Paragraph({ children: [] }),
    para(`Établi le ${today()} — à revoir au moins annuellement.`, 18),
  ];

  const doc = new Document({ creator: 'NIS365 Auditor', title: `Registre traitements - ${meta.orgName}`, features: { updateFields: true }, styles: AUTODOC_STYLES, sections: [{ children: [...cover, ...buildToc(), ...intro, ...body] }] });
  saveAs(await Packer.toBlob(doc), `Registre_Traitements_${safeName(meta.orgName)}_${new Date().toISOString().slice(0, 10)}.docx`);
}
